import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { AdminService } from '../../../core/services/admin.service';
import { AuthService } from '../../../core/services/auth.service';
import { ToastService } from '../../../core/services/toast.service';
import {
  AdminCustomerDto,
  PriceCalculationDto,
  ProductCheckResult,
  ProductImportResult,
  SubscriptionPlanDto,
  TenantPaymentDto,
  TenantStore,
} from '../../../core/models/admin.models';
import {
  ImportCurrencyOption,
  ImportPreviewFn,
  ImportStoreOption,
  ImportSubmitFn,
  ProductImportDialogComponent,
} from '../../../shared/components/product-import/product-import-dialog.component';
import { CurrencySettingsDto } from '../../../core/models/currency.models';
import {
  LucideAngularModule,
  UserPlus,
  Search,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  X,
  History,
  CalendarPlus,
  Eye,
  EyeOff,
  Pencil,
  Trash2,
  Package,
  ShieldCheck,
} from 'lucide-angular';

import { TranslatePipe } from '@ngx-translate/core';
import { DatePickerModule } from 'primeng/datepicker';

@Component({
  selector: 'app-admin-customers',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    LucideAngularModule,
    TranslatePipe,
    DatePickerModule,
    ProductImportDialogComponent,
  ],
  templateUrl: './customers.component.html',
})
export class AdminCustomersComponent implements OnInit {
  private adminService = inject(AdminService);
  private auth = inject(AuthService);
  private toast = inject(ToastService);
  private fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  // O'chirish tugmasi faqat SuperAdmin uchun ko'rinadi
  isSuperAdmin = computed(() => this.auth.currentUser()?.role === 'SuperAdmin');

  customers = signal<AdminCustomerDto[]>([]);
  total = signal(0);
  loading = signal(true);
  plans = signal<SubscriptionPlanDto[]>([]);

  page = signal(1);
  pageSize = 20;
  search = signal('');
  filterActive = signal<boolean | undefined>(undefined);

  showCreateDialog = signal(false);
  showSubscriptionDialog = signal(false);
  showExtendDialog = signal(false);
  showPaymentsDialog = signal(false);
  showEditDialog = signal(false);
  showDeleteDialog = signal(false);
  deleteConfirmText = signal('');
  showImportDialog = signal(false);
  tenantStores = signal<TenantStore[]>([]);
  selectedCustomer = signal<AdminCustomerDto | null>(null);
  submitting = signal(false);
  errorMsg = signal('');

  // Assign subscription  live price
  pricePreview = signal<PriceCalculationDto | null>(null);
  calcLoading = signal(false);

  // Extend subscription  live price
  extendPricePreview = signal<PriceCalculationDto | null>(null);
  extendMode = signal<'months' | 'days'>('months');
  extendCalcLoading = signal(false);

  // Payment history
  payments = signal<TenantPaymentDto[]>([]);
  paymentsLoading = signal(false);

  // Extend - date picker
  extendEndDateValue: Date | null = null;
  extendMonthlyValue = 1;
  minExtendDate = computed(() => {
    const c = this.selectedCustomer();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (c?.subscriptionEndDate) {
      const end = new Date(c.subscriptionEndDate);
      end.setHours(0, 0, 0, 0);
      return end > today ? end : today;
    }
    return today;
  });

  UserPlus = UserPlus;
  Search = Search;
  RefreshCw = RefreshCw;
  CheckCircle = CheckCircle;
  XCircle = XCircle;
  AlertCircle = AlertCircle;
  ChevronLeft = ChevronLeft;
  ChevronRight = ChevronRight;
  CreditCard = CreditCard;
  X = X;
  History = History;
  CalendarPlus = CalendarPlus;
  Eye = Eye;
  EyeOff = EyeOff;
  Pencil = Pencil;
  Trash2 = Trash2;
  Package = Package;
  ShieldCheck = ShieldCheck;

  phoneCode = '+998';
  showPassword = false;

  createForm = this.fb.group({
    fullName: ['', [Validators.required, Validators.minLength(2)]],
    phoneNumber: ['', [Validators.required, Validators.pattern(/^[0-9]{7,12}$/)]],
    password: ['', [Validators.required, Validators.minLength(6)]],
    gender: [0, Validators.required],
    businessName: [''],
    subscriptionPlanId: [''],
  });

  subscriptionForm = this.fb.group({
    subscriptionPlanId: ['', Validators.required],
    monthsDuration: [1, [Validators.required, Validators.min(1), Validators.max(12)]],
    notes: [''],
  });

  editForm = this.fb.group({
    isActive: [true, Validators.required],
    fullName: ['', Validators.required],
    phoneNumber: ['', Validators.required],
    password: ['', Validators.minLength(6)],
  });

  extendForm = this.fb.group({
    subscriptionPlanId: ['', Validators.required],
    monthsDuration: [1, [Validators.required, Validators.min(1), Validators.max(24)]],
    daysDuration: [1, [Validators.required, Validators.min(1), Validators.max(365)]],
    notes: [''],
  });

  ngOnInit() {
    this.loadCustomers();
    this.loadPlans();

    this.subscriptionForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const planId = this.subscriptionForm.value.subscriptionPlanId;
      const months = this.subscriptionForm.value.monthsDuration ?? 1;
      if (planId && months >= 1) this.calcPrice(planId, months);
      else this.pricePreview.set(null);
    });

    this.extendForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const planId = this.extendForm.value.subscriptionPlanId;
      const months = this.extendForm.value.monthsDuration ?? 1;
      const days = this.extendForm.value.daysDuration ?? 1;
      const duration = this.extendMode() === 'days' ? days : months;
      if (planId && duration >= 1 && this.showExtendDialog())
        this.calcExtendPrice(planId, duration);
      else this.extendPricePreview.set(null);
    });
  }

  calcPrice(planId: string, months: number) {
    this.calcLoading.set(true);
    this.adminService
      .calculatePrice(planId, months)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res.success) this.pricePreview.set(res.data);
          this.calcLoading.set(false);
        },
        error: () => this.calcLoading.set(false),
      });
  }

  calcExtendPrice(planId: string, durationValue: number) {
    this.extendCalcLoading.set(true);
    this.adminService
      .calculatePrice(planId, durationValue)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res.success) this.extendPricePreview.set(res.data);
          this.extendCalcLoading.set(false);
        },
        error: () => this.extendCalcLoading.set(false),
      });
  }

  loadCustomers() {
    this.loading.set(true);
    this.adminService
      .getCustomers({
        page: this.page(),
        pageSize: this.pageSize,
        search: this.search() || undefined,
        isActive: this.filterActive(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res.success) {
            this.customers.set(res.data.items);
            this.total.set(res.data.total);
          }
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  loadPlans() {
    this.adminService
      .getSubscriptionPlans()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        if (res.success) this.plans.set(res.data);
      });
  }

  onSearch(value: string) {
    this.search.set(value);
    this.page.set(1);
    this.loadCustomers();
  }
  setFilter(active: boolean | undefined) {
    this.filterActive.set(active);
    this.page.set(1);
    this.loadCustomers();
  }
  prevPage() {
    if (this.page() > 1) {
      this.page.update((p) => p - 1);
      this.loadCustomers();
    }
  }
  nextPage() {
    if (this.page() * this.pageSize < this.total()) {
      this.page.update((p) => p + 1);
      this.loadCustomers();
    }
  }
  totalPages() {
    return Math.ceil(this.total() / this.pageSize);
  }

  openCreate() {
    this.createForm.reset({ gender: 0, subscriptionPlanId: '' });
    this.phoneCode = '+998';
    this.showPassword = false;
    this.errorMsg.set('');
    this.showCreateDialog.set(true);
  }

  submitCreate() {
    if (this.createForm.invalid) return;
    this.submitting.set(true);
    this.errorMsg.set('');
    const v = this.createForm.value;
    this.adminService
      .createCustomer({
        fullName: v.fullName!,
        phoneNumber: this.phoneCode + v.phoneNumber!,
        password: v.password!,
        gender: v.gender!,
        businessName: v.businessName || undefined,
        subscriptionPlanId: v.subscriptionPlanId || undefined,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.submitting.set(false);
          if (res.success) {
            this.showCreateDialog.set(false);
            this.loadCustomers();
          } else this.errorMsg.set(res.message ?? res.errors?.join(' ') ?? 'Xato');
        },
        error: (err) => {
          this.submitting.set(false);
          this.errorMsg.set(err?.error?.message || 'Xatolik');
        },
      });
  }

  openSubscription(customer: AdminCustomerDto) {
    this.selectedCustomer.set(customer);
    this.subscriptionForm.reset({ monthsDuration: 1 });
    this.pricePreview.set(null);
    this.errorMsg.set('');
    this.showSubscriptionDialog.set(true);
  }

  submitSubscription() {
    if (this.subscriptionForm.invalid || !this.selectedCustomer()) return;
    this.submitting.set(true);
    this.errorMsg.set('');
    const v = this.subscriptionForm.value;
    this.adminService
      .assignSubscription(this.selectedCustomer()!.tenantId, {
        subscriptionPlanId: v.subscriptionPlanId!,
        monthsDuration: v.monthsDuration!,
        notes: v.notes || undefined,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.submitting.set(false);
          if (res.success !== false) {
            this.showSubscriptionDialog.set(false);
            this.loadCustomers();
          } else this.errorMsg.set(res.message ?? res.errors?.join(' ') ?? 'Xato');
        },
        error: (err) => {
          this.submitting.set(false);
          this.errorMsg.set(err?.error?.message || 'Xatolik');
        },
      });
  }

  openExtend(customer: AdminCustomerDto) {
    this.selectedCustomer.set(customer);
    const currentPlanId =
      this.plans().find((p) => p.name === customer.subscriptionPlanName)?.id ?? '';
    this.extendMode.set('months');
    this.extendMonthlyValue = 1;
    this.extendForm.reset({
      subscriptionPlanId: currentPlanId,
      monthsDuration: 1,
      daysDuration: 1,
    });
    this.extendEndDateValue = null;
    this.extendPricePreview.set(null);
    this.errorMsg.set('');
    this.showExtendDialog.set(true);
  }

  onExtendDateChange(date: Date | null) {
    this.extendEndDateValue = date;
    if (!date) {
      this.extendForm.patchValue({ daysDuration: 1 });
      return;
    }
    const days = this.computeDaysFromDate(date);
    this.extendForm.patchValue({ daysDuration: days });
  }

  computeDaysFromDate(endDate: Date): number {
    const c = this.selectedCustomer();
    const now = new Date();
    const startFrom =
      c?.subscriptionEndDate && new Date(c.subscriptionEndDate) > now
        ? new Date(c.subscriptionEndDate)
        : now;
    const diffMs = endDate.getTime() - startFrom.getTime();
    const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return Math.max(1, days);
  }

  computeMonthsFromDate(endDate: Date): number {
    const customer = this.selectedCustomer();
    const now = new Date();
    const startFrom =
      customer?.subscriptionEndDate && new Date(customer.subscriptionEndDate) > now
        ? new Date(customer.subscriptionEndDate)
        : now;
    const months =
      (endDate.getFullYear() - startFrom.getFullYear()) * 12 +
      (endDate.getMonth() - startFrom.getMonth());
    return Math.max(1, months);
  }

  getExtendMonthlySummary() {
    const customer = this.selectedCustomer();
    const currentEnd = customer?.subscriptionEndDate
      ? new Date(customer.subscriptionEndDate)
      : new Date();
    const now = new Date();
    const base = currentEnd > now ? currentEnd : now;
    const addedMonths = Math.max(1, this.extendMonthlyValue || 1);
    const daysToSubtract = Math.max(0, customer?.dailyExtensionDays ?? 0);
    const rawEnd = new Date(base);
    rawEnd.setMonth(rawEnd.getMonth() + addedMonths);
    const newEnd = new Date(rawEnd);
    newEnd.setDate(newEnd.getDate() - daysToSubtract);
    const daysLeft = Math.max(0, Math.ceil((currentEnd.getTime() - now.getTime()) / 86400000));
    return { currentEnd, newEnd, daysLeft, addedMonths, daysToSubtract };
  }

  increaseExtendMonths() {
    this.extendMonthlyValue = Math.min(24, (this.extendMonthlyValue || 1) + 1);
    this.extendForm.patchValue({ monthsDuration: this.extendMonthlyValue });
  }

  decreaseExtendMonths() {
    this.extendMonthlyValue = Math.max(1, (this.extendMonthlyValue || 1) - 1);
    this.extendForm.patchValue({ monthsDuration: this.extendMonthlyValue });
  }

  submitExtend() {
    if (this.extendForm.invalid || !this.selectedCustomer()) return;
    this.submitting.set(true);
    this.errorMsg.set('');
    const v = this.extendForm.value;
    const isDays = this.extendMode() === 'days';
    this.adminService
      .extendSubscription(this.selectedCustomer()!.tenantId, {
        subscriptionPlanId: v.subscriptionPlanId!,
        mode: this.extendMode(),
        monthsDuration: isDays ? undefined : v.monthsDuration!,
        daysDuration: isDays ? v.daysDuration! : undefined,
        notes: v.notes || undefined,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.submitting.set(false);
          if (res.success !== false) {
            this.toast.success(`${this.selectedCustomer()!.fullName} obunasi uzaytirildi`);
            this.showExtendDialog.set(false);
            this.loadCustomers();
          } else this.errorMsg.set(res.message ?? res.errors?.join(' ') ?? 'Xato');
        },
        error: (err) => {
          this.submitting.set(false);
          this.errorMsg.set(err?.error?.message || 'Xatolik');
        },
      });
  }

  openPayments(customer: AdminCustomerDto) {
    this.selectedCustomer.set(customer);
    this.payments.set([]);
    this.paymentsLoading.set(true);
    this.showPaymentsDialog.set(true);
    this.adminService
      .getPayments(customer.tenantId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res.success) this.payments.set(res.data);
          this.paymentsLoading.set(false);
        },
        error: () => this.paymentsLoading.set(false),
      });
  }

  openEdit(customer: AdminCustomerDto) {
    this.selectedCustomer.set(customer);
    this.editForm.reset({
      isActive: customer.isActive,
      fullName: customer.fullName,
      phoneNumber: customer.phoneNumber,
      password: '',
    });
    this.errorMsg.set('');
    this.showEditDialog.set(true);
  }

  submitEdit() {
    if (this.editForm.invalid || !this.selectedCustomer()) return;
    this.submitting.set(true);
    this.errorMsg.set('');
    const v = this.editForm.value;
    this.adminService
      .updateCustomer(this.selectedCustomer()!.tenantId, {
        isActive: v.isActive ?? true,
        fullName: v.fullName!,
        phoneNumber: v.phoneNumber!,
        password: v.password || undefined,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.submitting.set(false);
          if (res.success !== false) {
            this.showEditDialog.set(false);
            this.loadCustomers();
          } else this.errorMsg.set(res.message ?? res.errors?.join(' ') ?? 'Xato');
        },
        error: (err) => {
          this.submitting.set(false);
          this.errorMsg.set(err?.error?.message || 'Xatolik');
        },
      });
  }

  openDelete(customer: AdminCustomerDto) {
    this.selectedCustomer.set(customer);
    this.deleteConfirmText.set('');
    this.errorMsg.set('');
    this.showDeleteDialog.set(true);
  }

  confirmDelete() {
    const customer = this.selectedCustomer();
    if (!customer) return;
    // Tasdiqlash: biznes nomini aniq kiritish kerak
    if (this.deleteConfirmText().trim() !== customer.businessName) {
      this.errorMsg.set('Tasdiqlash uchun biznes nomini to‘g‘ri kiriting');
      return;
    }
    this.submitting.set(true);
    this.errorMsg.set('');
    this.adminService
      .deleteCustomer(customer.tenantId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.submitting.set(false);
          if (res.success !== false) {
            this.toast.success(
              `${customer.fullName} (${customer.businessName}) butunlay o‘chirildi`,
            );
            this.showDeleteDialog.set(false);
            this.loadCustomers();
          } else this.errorMsg.set(res.message ?? res.errors?.join(' ') ?? 'Xato');
        },
        error: (err) => {
          this.submitting.set(false);
          this.errorMsg.set(err?.error?.message || 'Xatolik');
        },
      });
  }

  // ----- Mahsulot import -----
  //
  // Oyna va ustun moslashtirish mantiqi `shared/components/product-import` da —
  // mijozning o'z do'konlar sahifasi ham shu komponentni ishlatadi. Bu yerda faqat
  // admin endpointlariga ulanish qoladi.

  /** Oynaga uzatiladigan do'konlar — `TenantStore` dan minimal shaklga. */
  importStoreOptions = computed<ImportStoreOption[]>(() =>
    this.tenantStores().map((s) => ({ id: s.id, name: s.name })),
  );

  /**
   * Maydon (arrow) sifatida saqlanadi: shablonda har tekshiruvda yangi funksiya
   * yaratilsa, `input` qiymati o'zgargan hisoblanib oyna cheksiz qayta chizilardi.
   */
  readonly importPreviewFn: ImportPreviewFn = (file) =>
    this.adminService.previewProductImport(file);

  readonly importSubmitFn: ImportSubmitFn = (file, mapping, storeId, currencies) =>
    this.adminService.importProducts(
      this.selectedCustomer()?.tenantId ?? '',
      file,
      mapping,
      storeId,
      currencies,
    );

  /**
   * MIJOZNING valyutalari — admin hisobiniki EMAS. Adminning o'z ro'yxati
   * ko'rsatilsa, mijozda yoqilmagan kod tanlanib import rad etilardi.
   */
  private customerCurrencies = signal<CurrencySettingsDto | null>(null);

  /** Faqat YOQILGAN valyutalar tanlanadi — o'chirilganida savdo qilib bo'lmaydi. */
  importCurrencyOptions = computed<ImportCurrencyOption[]>(() =>
    (this.customerCurrencies()?.currencies ?? [])
      .filter((c) => c.isActive)
      .map((c) => ({ label: `${c.code} — ${c.name}`, value: c.code })),
  );

  importDefaultCurrency = computed(() => this.customerCurrencies()?.quoteCurrency ?? null);

  openImport(customer: AdminCustomerDto) {
    this.selectedCustomer.set(customer);
    this.tenantStores.set([]);
    this.customerCurrencies.set(null);
    this.showImportDialog.set(true);

    this.adminService
      .getTenantStores(customer.tenantId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        if (res.success) this.tenantStores.set(res.data);
      });

    // Xato bo'lsa ro'yxat bo'sh qoladi va tanlagich ko'rsatilmaydi — import baribir
    // ishlaydi, backend mijozning kotirovka valyutasini oladi.
    this.adminService
      .getCustomerCurrencies(customer.tenantId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res.success) this.customerCurrencies.set(res.data);
        },
        error: () => this.customerCurrencies.set(null),
      });
  }

  onProductsImported(result: ProductImportResult) {
    if (result.createdCount > 0) {
      this.toast.success(`${result.createdCount} ta mahsulot yaratildi`);
    }
  }

  // ----- Katalog tekshiruvi -----
  //
  // Import validatsiyasi keyinroq qo'shilgan, shuning uchun undan oldin yaratilgan
  // mahsulotlarda narxsiz, birliksiz yoki takroriy yozuvlar qolgan bo'lishi mumkin.
  // Bu tugma shularni topib beradi — hech narsani o'zgartirmaydi.

  showCheckDialog = signal(false);
  checking = signal(false);
  checkResult = signal<ProductCheckResult | null>(null);
  checkCopied = signal(false);

  openCheck(customer: AdminCustomerDto) {
    this.selectedCustomer.set(customer);
    this.checkResult.set(null);
    this.checkCopied.set(false);
    this.errorMsg.set('');
    this.showCheckDialog.set(true);
    this.checking.set(true);

    this.adminService
      .checkProducts(customer.tenantId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.checking.set(false);
          if (res.success !== false && res.data) this.checkResult.set(res.data);
          else this.errorMsg.set(res.message ?? res.errors?.join(' ') ?? 'Xatolik');
        },
        error: (err) => {
          this.checking.set(false);
          this.errorMsg.set(err?.error?.message || 'Xatolik yuz berdi');
        },
      });
  }

  /**
   * Hisobotni matn ko'rinishida nusxalaydi — admin uni mijozga o'girib yuboradi.
   * Fayl yuklashdan ko'ra shu qulay: xabar odatda Telegramga tashlanadi.
   */
  copyCheckReport() {
    const result = this.checkResult();
    const customer = this.selectedCustomer();
    if (!result) return;

    const lines = [
      `${customer?.businessName ?? ''} — mahsulotlar tekshiruvi`,
      `Jami: ${result.totalProducts} ta, muammoli: ${result.problemProductCount} ta`,
      '',
      ...result.issues.map(
        (i) => `• ${i.name}${i.brand ? ` (${i.brand})` : ''} [${i.barcode}] — ${i.message}`,
      ),
    ];

    void navigator.clipboard.writeText(lines.join('\n')).then(
      () => {
        this.checkCopied.set(true);
        this.toast.success('Hisobot nusxalandi');
      },
      () => this.toast.error('Nusxalab bo‘lmadi'),
    );
  }

  formatDate(d: string | null): string {
    if (!d) return '';
    return new Date(d).toLocaleDateString('uz-UZ');
  }

  formatMoney(n: number): string {
    return n.toLocaleString('uz-UZ') + ' UZS';
  }
}
